import { keepPreviousData, queryOptions } from "@tanstack/react-query"

import type { ProjectClientRateStatus } from "@repo/supabase/database-types"
import { getIsomorphicSupabaseClient } from "@repo/supabase/isomorphic-client"
import type { DataTableBaseSearch } from "~/lib/schemas/common"
import { createProjectsService } from "./projects.service"

export const getExtendedProjectQueryOptions = ({
  projectId,
}: {
  projectId: string
}) =>
  queryOptions({
    queryKey: ["extended-projects", projectId],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const projectsService = createProjectsService(supabase)
      return projectsService.getOneExtended({ projectId })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  })

export const getExtendedProjectsQueryOptions = ({
  filters,
}: {
  filters?: DataTableBaseSearch
} = {}) =>
  queryOptions({
    queryKey: filters ? ["extended-projects", filters] : ["extended-projects"],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const projectsService = createProjectsService(supabase)
      return projectsService.getManyExtended(filters)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
  })

export const getProjectClientRatesQueryOptions = ({
  projectId,
  status,
}: {
  projectId: string
  status?: ProjectClientRateStatus[]
}) =>
  queryOptions({
    queryKey: ["project-client-rates", projectId, status],
    queryFn: async () => {
      const supabase = await getIsomorphicSupabaseClient()
      const projectsService = createProjectsService(supabase)
      return projectsService.getClientRates({ projectId, status })
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  })
